/** 従業員一覧の右パネル。基本情報の要点と「全画面で開く」。編集は Drawer(既存フォーム)。 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useApi } from "../../lib/useApi";
import { Initials, KeyValueList, PanelSection, SidePanel, StatusTag } from "../../ui";
import { EmployeeFormModal } from "./EmployeeForm";
import { employeeStatusTone, statusLabel } from "./constants";
import { errorText, useCustomFields, useHrPerms } from "./hooks";
import { useEmployeeLoans } from "./loanHooks";

export function EmployeePanel({ id, onClose }: { id: number; onClose: () => void }) {
  const api = useApi();
  const { canWrite } = useHrPerms();
  const [editing, setEditing] = useState(false);
  const fields = useCustomFields().data?.items ?? [];
  const query = useQuery({
    queryKey: ["hr", "employee", id],
    queryFn: () => api.employee(id),
    enabled: Number.isFinite(id),
  });
  const loans = useEmployeeLoans(Number.isFinite(id) ? id : undefined);
  const emp = query.data;
  const openLoans = (loans.data?.items ?? []).filter((l) => l.open);

  if (query.isError) {
    return (
      <SidePanel title="従業員" onClose={onClose}>
        <Alert type="error" showIcon message="従業員を取得できなかった" description={errorText(query.error)} />
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={emp?.name ?? "従業員"}
      loading={query.isLoading || !emp}
      avatar={emp && <Initials name={emp.name} />}
      status={
        emp && (
          <StatusTag tone={employeeStatusTone(emp.status)} dot>
            {statusLabel(emp.status)}
          </StatusTag>
        )
      }
      sub={
        emp && (
          <>
            <div>
              {[emp.emp_code, emp.org_unit_path, emp.position_name].filter(Boolean).join(" · ")}
            </div>
            {emp.email && <div>{emp.email}</div>}
          </>
        )
      }
      fullHref={`/hr/employees/${id}`}
      onClose={onClose}
      actions={
        canWrite ? (
          <Button size="small" onClick={() => setEditing(true)}>
            編集
          </Button>
        ) : undefined
      }
    >
      {emp && (
        <>
          <PanelSection title="基本情報">
            <KeyValueList
              narrow
              items={[
                { label: "雇用区分", value: emp.employment_type },
                { label: "入社日", value: emp.joined_at },
                { label: "上長", value: emp.manager_name },
                {
                  label: "兼務",
                  value: emp.concurrent.length
                    ? emp.concurrent.map((c) => c.unit_name).join("、")
                    : "",
                },
                { label: "会社携帯", value: emp.work_mobile },
                { label: "会社電話", value: emp.work_phone },
                { label: "内線", value: emp.extension },
                ...fields.map((f) => ({ label: f.label, value: emp.extra?.[f.key] ?? "" })),
              ]}
            />
          </PanelSection>
          <PanelSection title="貸与中">
            {openLoans.length ? (
              <div>{openLoans.map((l) => l.item).join("、")}</div>
            ) : (
              <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
            )}
          </PanelSection>
          {emp.name_needs_review && (
            <Alert
              type="warning"
              showIcon
              message="氏名が姓・名に割れていない"
              description={
                <>
                  <Link to={`/hr/employees/${id}`}>全画面で開いて</Link>直す。
                </>
              }
            />
          )}
          <EmployeeFormModal as="drawer" open={editing} employee={emp} onClose={() => setEditing(false)} />
        </>
      )}
    </SidePanel>
  );
}
